import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, FolderOpen, FolderSimplePlus, CircleNotch, DownloadSimple, Trash, FileArrowUp, LockSimple, PencilSimple, Check, ArrowsLeftRight, Eye, NotePencil, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import api from "../lib/api";
import { FilePreview, previewableExt } from "./FilePreview";

const CHUNK = 4 * 1024 * 1024;
const EXTS = ["pdf", "doc", "docx", "dwg", "dxf", "png", "jpg", "jpeg"];

const fmtSize = (b) => {
  if (!b) return "0 KB";
  if (b >= 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};

export const DossierModal = ({ machine, plan, onClose }) => {
  const { t, lang } = useLang();
  const d = t.dossier;
  const [data, setData] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [editing, setEditing] = useState(null); // {id|null, name, parentId}
  const [movingFile, setMovingFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [noteEdit, setNoteEdit] = useState(null); // {id, text}
  const [query, setQuery] = useState("");

  const saveNote = async () => {
    try {
      await api.put(`/machines/${machine.id}/dossier/${noteEdit.id}/note`, { note: noteEdit.text });
      toast.success(d.noteSaved);
      setNoteEdit(null);
      load();
    } catch (e) { toast.error(d.uploadError); }
  };
  const [zipping, setZipping] = useState(false);
  const [reporting, setReporting] = useState(false);

  const downloadReport = async () => {
    setReporting(true);
    try {
      const res = await api.get(`/machines/${machine.id}/dossier/report?lang=${lang}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `report-fascicolo-${machine.serial_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(d.reportStarted);
    } catch (e) { toast.error(d.downloadError); }
    finally { setReporting(false); }
  };

  const downloadZip = async () => {
    setZipping(true);
    try {
      const res = await api.get(`/machines/${machine.id}/dossier/archive?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fascicolo-${machine.serial_number}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(d.zipStarted);
    } catch (e) { toast.error(d.downloadError); }
    finally { setZipping(false); }
  };
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);
  const hasArchive = !!plan?.fascicolo_package;
  const presets = Object.values(d.categories);
  const tooLargeMsg = () => d.tooLarge.replace("{max}", fmtSize((data && data.max_file) || 104857600));
  const quotaFullMsg = () => d.quotaFull.replace("{quota}", fmtSize((data && data.quota) || 5368709120));

  const load = () => {
    api.get(`/machines/${machine.id}/dossier`).then(({ data }) => {
      setData(data);
      if (data.folders.length && !data.folders.find((f) => f.id === selectedFolder)) setSelectedFolder(data.folders[0].id);
    }).catch(() => setData({ files: [], folders: [], used: 0, quota: 0 }));
  };
  useEffect(() => { if (hasArchive) load(); }, [machine.id, hasArchive]);

  const saveFolder = async () => {
    const name = editing?.name?.trim();
    if (!name) return;
    try {
      if (editing.id) {
        await api.put(`/machines/${machine.id}/dossier/folders/${editing.id}`, { name });
        toast.success(d.folderRenamed);
      } else {
        const { data: rec } = await api.post(`/machines/${machine.id}/dossier/folders`, { name, parent_id: editing.parentId || null });
        setSelectedFolder(rec.id);
        toast.success(d.folderCreated);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "max_depth" ? d.maxDepth : d.uploadError);
    }
  };

  const moveFile = async (fileId, folderId) => {
    try {
      await api.put(`/machines/${machine.id}/dossier/${fileId}/move`, { folder_id: folderId });
      toast.success(d.moved);
      setMovingFile(null);
      load();
    } catch (e) { toast.error(d.uploadError); }
  };

  const deleteFolder = async (f) => {
    try {
      await api.delete(`/machines/${machine.id}/dossier/folders/${f.id}`);
      toast.success(d.folderDeleted);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "folder_not_empty" ? d.folderNotEmpty : d.uploadError);
    }
  };

  const upload = async (file) => {
    if (!file || !selectedFolder) return;
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!EXTS.includes(ext)) { toast.error(d.badFormat); return; }
    if (file.size > ((data && data.max_file) || 100 * 1024 * 1024)) { toast.error(tooLargeMsg()); return; }
    if (data && data.used + file.size > data.quota) { toast.error(quotaFullMsg()); return; }
    const uploadId = crypto.randomUUID();
    const chunks = Math.max(1, Math.ceil(file.size / CHUNK));
    setProgress(0);
    try {
      for (let i = 0; i < chunks; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await api.post(`/machines/${machine.id}/dossier/chunk`, fd);
        setProgress(Math.round(((i + 1) / (chunks + 1)) * 100));
      }
      await api.post(`/machines/${machine.id}/dossier/complete`, { upload_id: uploadId, folder_id: selectedFolder, file_name: file.name });
      toast.success(d.uploaded);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det === "dossier_quota_exceeded" ? quotaFullMsg() : det === "file_too_large" ? tooLargeMsg() : det === "fascicolo_required" ? d.required : d.uploadError);
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/machines/${machine.id}/dossier/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(d.downloadError); }
  };

  const removeFile = async (f) => {
    if (!window.confirm(d.confirmDelete)) return;
    try {
      await api.delete(`/machines/${machine.id}/dossier/${f.id}`);
      load();
      toast.success(d.deleted);
    } catch (e) { toast.error(d.uploadError); }
  };

  const pct = data && data.quota ? Math.min(100, (data.used / data.quota) * 100) : 0;
  const legacy = data ? data.files.filter((f) => !f.folder_id) : [];
  const flatFolders = [];
  if (data) {
    const walk = (parent, depth) => {
      data.folders.filter((f) => (f.parent_id || null) === parent).forEach((f) => {
        flatFolders.push({ ...f, depth });
        walk(f.id, depth + 1);
      });
    };
    walk(null, 0);
  }
  const hasChildren = (id) => data && data.folders.some((f) => f.parent_id === id);

  const q = query.trim().toLowerCase();
  const searchResults = data && q ? data.files.filter((f) => f.file_name.toLowerCase().includes(q) || (f.note || "").toLowerCase().includes(q)) : null;

  const fileRow = (f, i) => (
    <li key={f.id} className="py-2.5 pl-7" data-testid={`dossier-file-${i}`}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[#0F172A] truncate">{f.file_name}</p>
          <p className="text-[11px] text-slate-400 font-mono-tech">{fmtSize(f.size)}{f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString()}` : ""}</p>
          {f.note && noteEdit?.id !== f.id && (
            <p className="text-[11px] text-slate-500 italic truncate" data-testid={`dossier-note-${i}`}>{f.note}</p>
          )}
        </div>
        {previewableExt(f.file_name) && movingFile !== f.id && (
          <button onClick={() => setPreview(f)} className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5" title={d.preview} data-testid={`dossier-preview-${i}`}><Eye size={17} /></button>
        )}
        <button onClick={() => setNoteEdit({ id: f.id, text: f.note || "" })} className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5" title={d.noteLabel} data-testid={`dossier-note-btn-${i}`}><NotePencil size={17} /></button>
        {movingFile === f.id ? (
          <select autoFocus defaultValue="" onChange={(e) => e.target.value && moveFile(f.id, e.target.value)} onBlur={() => setMovingFile(null)}
            className="rounded-md border border-emerald-500 px-2 py-1.5 text-xs text-[#0F172A] bg-white outline-none max-w-[180px]" data-testid={`dossier-move-select-${i}`}>
            <option value="" disabled>{d.moveFile}</option>
            {flatFolders.filter((fo) => fo.id !== f.folder_id).map((fo) => (
              <option key={fo.id} value={fo.id}>{"— ".repeat(fo.depth)}{fo.name}</option>
            ))}
          </select>
        ) : (
          <button onClick={() => setMovingFile(f.id)} className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5" title={d.moveFile} data-testid={`dossier-move-${i}`}><ArrowsLeftRight size={16} /></button>
        )}
        <button onClick={() => download(f)} className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5" title={d.download} data-testid={`dossier-download-${i}`}><DownloadSimple size={17} /></button>
        <button onClick={() => removeFile(f)} className="text-slate-300 hover:text-red-500 transition-colors p-1.5" title={d.delete} data-testid={`dossier-delete-${i}`}><Trash size={17} /></button>
      </div>
      {noteEdit?.id === f.id && (
        <div className="mt-1.5 flex items-center gap-2" data-testid={`dossier-note-editor-${i}`}>
          <input autoFocus value={noteEdit.text} maxLength={300}
            onChange={(e) => setNoteEdit({ ...noteEdit, text: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && saveNote()}
            placeholder={d.notePlaceholder}
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs text-[#0F172A] outline-none focus:border-emerald-500"
            data-testid={`dossier-note-input-${i}`} />
          <button onClick={saveNote} className="rounded-full bg-emerald-500 p-2 text-[#04170F] hover:bg-emerald-400 transition-colors" data-testid={`dossier-note-save-${i}`}><Check size={13} weight="bold" /></button>
          <button onClick={() => setNoteEdit(null)} className="rounded-full border border-slate-300 p-2 text-slate-500" data-testid={`dossier-note-cancel-${i}`}><X size={13} /></button>
        </div>
      )}
    </li>
  );

  const renderFolder = (folder, fi, depth) => {
    const files = data.files.filter((f) => f.folder_id === folder.id);
    const children = data.folders.filter((f) => f.parent_id === folder.id);
    return (
      <div key={folder.id} className="rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2" style={{ marginLeft: depth * 14 }} data-testid={`dossier-folder-${fi}`}>
        <div className="flex items-center gap-2">
          <FolderOpen size={16} className="text-emerald-600 shrink-0" />
          <span className="flex-1 text-sm font-semibold text-[#0F172A] truncate">{folder.name}</span>
          <span className="font-mono-tech text-[10px] text-slate-400">{files.length} file</span>
          {depth < 2 && (
            <button onClick={() => setEditing({ id: null, name: "", parentId: folder.id })} className="text-slate-400 hover:text-emerald-600 p-1" title={d.subFolder} data-testid={`dossier-folder-sub-${fi}`}><FolderSimplePlus size={14} /></button>
          )}
          <button onClick={() => setEditing({ id: folder.id, name: folder.name })} className="text-slate-400 hover:text-emerald-600 p-1" title={d.rename} data-testid={`dossier-folder-rename-${fi}`}><PencilSimple size={14} /></button>
          {files.length === 0 && children.length === 0 && (
            <button onClick={() => deleteFolder(folder)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`dossier-folder-delete-${fi}`}><Trash size={14} /></button>
          )}
        </div>
        {files.length > 0 && <ul className="mt-1 divide-y divide-slate-100">{files.map((f, i) => fileRow(f, `${fi}-${i}`))}</ul>}
        {children.length > 0 && <div className="mt-1.5 space-y-1">{children.map((c, ci) => renderFolder(c, `${fi}-${ci}`, depth + 1))}</div>}
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0F1A]/50 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white rounded-md border border-slate-200 max-h-[92vh] flex flex-col" data-testid="dossier-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-display font-bold text-xl text-[#0F172A] flex items-center gap-2"><FolderOpen size={20} className="text-emerald-600" /> {d.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{machine.name} · {d.regulation}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-[#0F172A]" data-testid="dossier-modal-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600" data-testid="dossier-private-note">
            <LockSimple size={14} className="shrink-0 mt-0.5 text-slate-500" /> {d.privateNote}
          </div>

          {!hasArchive ? (
            <div className="mt-5 rounded-md border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900" data-testid="dossier-archive-required">
              {d.required}
            </div>
          ) : !data ? (
            <div className="flex justify-center py-10"><CircleNotch size={26} className="text-emerald-500 animate-spin" /></div>
          ) : (
            <>
              <datalist id="dossier-folder-presets">
                {presets.map((p) => <option key={p} value={p} />)}
              </datalist>

              <div className="mt-4" data-testid="dossier-quota">
                <div className="flex justify-between text-[11px] font-mono-tech text-slate-500">
                  <span>{d.space}</span>
                  <span data-testid="dossier-quota-used">{fmtSize(data.used)} / {fmtSize(data.quota)}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                  <div className={`h-full rounded-full ${pct > 90 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.max(pct, 1)}%` }} />
                </div>
              </div>

              {/* Barra cartelle */}
              <div className="mt-5 flex items-center justify-between gap-2">
                <p className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">{d.foldersLabel}</p>
                <div className="flex items-center gap-2">
                  {data.files.length > 0 && (
                    <>
                      <button onClick={downloadReport} disabled={reporting}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors disabled:opacity-60"
                        data-testid="dossier-report-btn">
                        {reporting ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} />} {d.reportBtn}
                      </button>
                      <button onClick={downloadZip} disabled={zipping}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors disabled:opacity-60"
                        data-testid="dossier-zip-btn">
                        {zipping ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} />} {d.zipBtn}
                      </button>
                    </>
                  )}
                  <button onClick={() => setEditing({ id: null, name: "", parentId: null })}
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500 px-3.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
                    data-testid="dossier-new-folder">
                    <FolderSimplePlus size={14} /> {d.newFolder}
                  </button>
                </div>
              </div>

              {data.files.length > 0 && (
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={d.search}
                  className="mt-3 w-full rounded-md border border-slate-200 px-3.5 py-2 text-sm text-[#0F172A] outline-none focus:border-emerald-500"
                  data-testid="dossier-search-input" />
              )}

              {editing && (
                <div className="mt-3 flex items-center gap-2" data-testid="dossier-folder-editor">
                  <input autoFocus list="dossier-folder-presets" value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && saveFolder()}
                    placeholder={d.folderName}
                    className="flex-1 rounded-md border border-slate-300 px-3.5 py-2 text-sm text-[#0F172A] outline-none focus:border-emerald-500"
                    data-testid="dossier-folder-name-input" />
                  <button onClick={saveFolder} className="rounded-full bg-emerald-500 p-2.5 text-[#04170F] hover:bg-emerald-400 transition-colors" data-testid="dossier-folder-save"><Check size={15} weight="bold" /></button>
                  <button onClick={() => setEditing(null)} className="rounded-full border border-slate-300 p-2.5 text-slate-500" data-testid="dossier-folder-cancel"><X size={15} /></button>
                </div>
              )}

              {data.folders.length === 0 && !editing ? (
                <p className="mt-4 py-6 text-center text-sm text-slate-500" data-testid="dossier-no-folders">{d.noFolders}</p>
              ) : (
                <>
                  {/* Upload */}
                  {data.folders.length > 0 && (
                    <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                      <div>
                        <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{d.uploadInto}</label>
                        <select value={selectedFolder} onChange={(e) => setSelectedFolder(e.target.value)}
                          className="w-full rounded-md border border-slate-300 px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500 bg-white"
                          data-testid="dossier-folder-select">
                          {flatFolders.map((f) => <option key={f.id} value={f.id}>{"— ".repeat(f.depth)}{f.name}</option>)}
                        </select>
                      </div>
                      <label className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold cursor-pointer transition-colors ${progress !== null ? "bg-slate-200 text-slate-500 pointer-events-none" : "bg-emerald-500 text-[#04170F] hover:bg-emerald-400"}`} data-testid="dossier-upload-btn">
                        {progress !== null ? <><CircleNotch size={16} className="animate-spin" /> {progress}%</> : <><FileArrowUp size={16} /> {d.upload}</>}
                        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.dwg,.dxf,.png,.jpg,.jpeg" className="hidden"
                          onChange={(e) => upload(e.target.files[0])} data-testid="dossier-file-input" />
                      </label>
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-slate-400">{d.formats}</p>

                  {/* Albero cartelle / risultati ricerca */}
                  {searchResults ? (
                    searchResults.length ? (
                      <ul className="mt-4 divide-y divide-slate-100" data-testid="dossier-search-results">
                        {searchResults.map((f, i) => fileRow(f, `search-${i}`))}
                      </ul>
                    ) : (
                      <p className="mt-4 py-6 text-center text-sm text-slate-500" data-testid="dossier-search-empty">{d.noResults}</p>
                    )
                  ) : (
                    <>
                      <div className="mt-4 space-y-1" data-testid="dossier-folder-tree">
                        {data.folders.filter((f) => !f.parent_id).map((folder, fi) => renderFolder(folder, fi, 0))}
                      </div>

                      {legacy.length > 0 && (
                        <ul className="mt-3 divide-y divide-slate-100" data-testid="dossier-file-list">{legacy.map((f, i) => fileRow(f, `legacy-${i}`))}</ul>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {preview && <FilePreview file={preview} machineId={machine.id} onClose={() => setPreview(null)} />}
      </motion.div>
    </motion.div>
  );
};
